import React, { useEffect, useMemo, useState } from 'react';
import { Collapse, Icon, Tooltip, Select, DatePicker } from 'antd';
import {
  Button,
  Table,
  Widget,
  ColumnProps,
  OptionProps,
  Form,
  Row,
  FormInput,
  Col,
  FormResetButton,
  FormSelect,
} from '@digital/ui';
import moment from 'moment';
const { Panel } = Collapse;

import { hasPermission, hasPermissionPerfil } from '@app/helpers/permissions';
import {
  ExportarOrdemDeCompra,
  FitroOrdemCompraPorStatusCodigo,
  getAllOrdemCompraPorStatusCodigo,
  getAllOrdemCompraPorStatusCodigoWithParams,
  OrdemCompraResult,
  verificaUsuarioPodeAcessarOrdemCompra,
} from '@app/api/sas/ordemCompra';
import { compareDates, compareNumber, compareText } from '@app/helpers/comparators';
import RelatorioOrdemCompraFiltros from './RelatorioOrdemCompraFiltros';
import { isValidGuid } from '@app/helpers/geradorGuid';
import { EGuidPerfilAcesso } from '@app/enums/EGuidPerfilAcesso';
import { Visibility } from '@app/components';
import { mountFormFilterDropDown } from '@app/utils/formFilterDropDown';
import { getAllStatusOrdemCompraExibicaoOptions } from '@app/api/status';
import ModalAtualizarStatusOrdemCompra from './ModalAtualizarStatusOrdemCompra';
import { UUID } from 'crypto';
import { convertUTCtoLocal, dateTimeFormat } from '@app/helpers/dateTimeFormats';
import ModalCadastrarConsultiva from './ModalCadastrarConsultiva';
const { Option } = Select;

interface TabelaOrdemCompraProps {
  widgetTitle: string;
  dataSource: OrdemCompraResult[];
  resultaTabelaStatusConcluido: boolean;
  optionPropsCompradores: OptionProps[];
  updateUsuarioCompradorOrdemCompra(ordemCompraId: string, usuarioCompradorId: string): void;
  onUpdate(filtros: FitroOrdemCompraPorStatusCodigo): void;
  fetchData: (data: any) => void;
  handleExportar: (data: any) => void;
  currentPage: number;
  pageSize: number;
  totalPage: number;
  totalItems: number;
  exportando: boolean;
  periodoInicio: any;
  periodoFim: any;
  setPeriodoInicio: (value: any) => void;
  setPeriodoFim: (value: any) => void;
}

type OptionType = {
  text: string;
  value: UUID;
};

const STORAGE_KEY = 'visibleColumnsTableOrdemCompra';

const defaultColumns = [
  'oc',
  'demandaIdentificador',
  'diretoria',
  'area',
  'departamento',
  'demanda',
  'tipo',
  'numeroContrato',
  'modalidadeContratoTotvs',
  'tipoServico',
  'valorOrdemCompra',
  'ptr',
  'compradorId',
  'statusPrazo',
  'statusOrdemCompraExibicao',
  'ddi',
  'isEmergencialString',
  'dataOc',
  'escopo',
  'fornecedorNome',
  'updatedAt',
];

const columnOptions = [
  { label: 'OC', value: 'oc' },
  { label: 'Demanda', value: 'demandaIdentificador' },
  { label: 'DIR.', value: 'diretoria' },
  { label: 'ÁREA', value: 'area' },
  { label: 'DEPART.', value: 'departamento' },
  { label: 'DEMANDANTE', value: 'demanda' },
  { label: 'TIPO', value: 'tipo' },
  { label: 'Numero Contrato/Pcs.', value: 'numeroContrato' },
  { label: 'Modalidade Contrato Totvs', value: 'modalidadeContratoTotvs' },
  { label: 'TIPO SERVIÇO', value: 'tipoServico' },
  { label: 'VALOR OC', value: 'valorOrdemCompra' },
  { label: 'PTR', value: 'ptr' },
  { label: 'COMPRADOR', value: 'compradorId' },
  { label: 'STATUS PRAZO', value: 'statusPrazo' },
  { label: 'STATUS PROCESSO COMPRA', value: 'statusOrdemCompraExibicao' },
  { label: 'DDI', value: 'ddi' },
  { label: 'OC EMER.', value: 'isEmergencialString' },
  { label: 'DATA DA OC', value: 'dataOc' },
  { label: 'Escopo Resumido', value: 'escopo' },
  { label: 'Fornecedor', value: 'fornecedorNome' },
  { label: 'Data Conclusão', value: 'updatedAt' }
];

const getInitialColumns = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return defaultColumns;
    }
  }
  return defaultColumns;
};

const TabelaOrdemCompra = ({
  widgetTitle,
  dataSource,
  resultaTabelaStatusConcluido,
  optionPropsCompradores,
  updateUsuarioCompradorOrdemCompra,
  onUpdate,
  currentPage,
  pageSize,
  totalPage,
  totalItems,
  fetchData,
  handleExportar,
  exportando,
  periodoInicio,
  periodoFim,
  setPeriodoInicio,
  setPeriodoFim,
}: TabelaOrdemCompraProps) => {
  const [visibleColumns, setVisibleColumns] = useState<string[]>(getInitialColumns);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);
  const [tableFilters, setTableFilters] = useState<any>({});
  const [tableSorter, setTableSorter] = useState<any>({});

  const [modalAtualizarOrdemCompra, setModalAtualizarOrdemCompra] = useState(false);

  const [modalCadastrarConsultiva, setModalCadastrarConsultiva] = useState(false);

  const [ordemCompraId, setOrdemCompraId] = useState('');

  const [statusOptions, setStatusOptions] = useState<OptionType[]>([]);

  useEffect(() => {
    getAllStatusOrdemCompraExibicaoOptions().then(res => {
      if (res) {
        setStatusOptions(res);
      }
    });
  }, []);

  const columns = useMemo(() => {
    const columns: ColumnProps<OrdemCompraResult>[] = ([] = new Array());
    columns.push({
      title: 'OC',
      dataIndex: 'oc',
      fixed: 'left',
      sorter: (a, b) => compareNumber(a.oc, b.oc),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="oc"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'Demanda',
      dataIndex: 'demandaIdentificador',
      sorter: (a, b) => compareText(a.diretoria, b.diretoria),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="diretoria"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'DIR.',
      dataIndex: 'diretoria',
      sorter: (a, b) => compareText(a.diretoria, b.diretoria),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="diretoria"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'ÁREA',
      dataIndex: 'area',
      sorter: (a, b) => compareText(a.area, b.area),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="area"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'DEPART.',
      dataIndex: 'departamento',
      sorter: (a, b) => compareText(a.departamento, b.departamento),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="departamento"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'DEMANDANTE',
      dataIndex: 'demanda',
      sorter: (a, b) => compareText(a.demanda, b.demanda),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="demanda"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });
    columns.push({
      title: 'TIPO',
      dataIndex: 'tipo',
      sorter: (a, b) => compareText(a.tipo, b.tipo),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Selecionar"
            style={{ width: 180, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
            onChange={value => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            allowClear>
            <Select.Option value="1">Nova Contratação</Select.Option>
            <Select.Option value="2">Aditivo</Select.Option>
            <Select.Option value="4">Faturamento Direto</Select.Option>
          </Select>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>
            Limpar
          </Button>
        </div>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'Numero Contrato/Pcs.',
      dataIndex: 'numeroContrato',
      sorter: (a, b) => compareText(a.diretoria, b.diretoria),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="diretoria"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'Modalidade Contrato Totvs',
      dataIndex: 'modalidadeContratoTotvs',
      sorter: (a, b) => compareText(a.modalidadeContratoTotvs, b.modalidadeContratoTotvs),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Selecionar"
            style={{ width: 180, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
            onChange={value => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            allowClear>
            <Select.Option value="PCS">PCS</Select.Option>
            <Select.Option value="Contrato">Contrato</Select.Option>
          </Select>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>
            Limpar
          </Button>
        </div>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'TIPO SERVIÇO',
      dataIndex: 'tipoServico',
      sorter: (a, b) => compareText(a.tipoServico, b.tipoServico),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="tipoServico"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'Fornecedor',
      dataIndex: 'fornecedorNome',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="fornecedorNome"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'Escopo Resumido',
      dataIndex: 'escopo',
      render: (index, record) => {
        const text = record.escopo || '';
        const truncated = text.length > 6 ? text.slice(0, 6) + '...' : text;
        return <Tooltip title={text}>{truncated}</Tooltip>;
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="escopo"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'VALOR OC',
      dataIndex: 'valorOrdemCompra',
      sorter: (a, b) => compareText(a.valorOrdemCompra, b.valorOrdemCompra),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="valorOrdemCompra"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });
    columns.push({
      title: 'PTR',
      dataIndex: 'ptr',
      sorter: (a, b) => compareText(a.ptr, b.ptr),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Selecionar"
            style={{ width: 160, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
            onChange={value => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            allowClear
            disabled={!hasPermission(['SAS_ALTERAR_COMPRADOR'])}>
            <Select.Option value="Sim">Sim</Select.Option>
            <Select.Option value="Não">Não</Select.Option>
          </Select>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>
            Limpar
          </Button>
        </div>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

     columns.push({
      title: 'COMPRADOR',
      dataIndex: 'compradorId',
      sorter: (a, b) => compareText(a.compradorId, b.compradorId),

      filters: optionPropsCompradores || [],

      filterMultiple: true,

      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) => value != "" && value == record.compradorId,

      render: (text, record, index) => {
        return (
          <Select
            defaultValue={record.compradorId}
            style={{ width: '100%' }}
            onChange={e => {
              record.compradorId = e;
              updateUsuarioCompradorOrdemCompra(record.id, record.compradorId);
            }}
            disabled={!hasPermission(['SAS_ALTERAR_COMPRADOR']) || resultaTabelaStatusConcluido}>
            {optionPropsCompradores.map(option => {
              return <Option value={option.value}>{option.text}</Option>;
            })}
          </Select>
        );
      },
    });

    columns.push({
      title: 'STATUS PRAZO',
      dataIndex: 'statusPrazo',
      sorter: (a, b) => compareText(a.statusPrazo, b.statusPrazo),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Selecionar"
            style={{ width: 160, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
            onChange={value => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            allowClear>
            <Select.Option value="Em dia">Em dia</Select.Option>
            <Select.Option value="Atrasado">Atrasado</Select.Option>
          </Select>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>
            Limpar
          </Button>
        </div>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });
    columns.push({
      title: 'STATUS PROCESSO COMPRA',
      dataIndex: 'statusOrdemCompraExibicao',
      sorter: (a, b) => compareText(a.statusOrdemCompraExibicao, b.statusOrdemCompraExibicao),
       render: (_, record) => record.statusOrdemCompraExibicao || record.statusOrdemCompra,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Selecionar"
            style={{ width: 200, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
            onChange={value => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            allowClear>
            {statusOptions.map(opt => (
              <Select.Option value={opt.value}>
                {opt.text}
              </Select.Option>
              
            ))}
          </Select>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>
            Limpar
          </Button>
        </div>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'DDI',
      dataIndex: 'ddi',
      sorter: (a, b) => compareText(a.ddi, b.ddi),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            placeholder="Selecionar"
            style={{ width: 160, marginBottom: 8, display: 'block' }}
            value={selectedKeys[0]}
            onChange={value => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            allowClear>
            <Select.Option value="true">Elegível</Select.Option>
            <Select.Option value="false">Não Elegível</Select.Option>
          </Select>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>
            Limpar
          </Button>
        </div>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'OC EMER.',
      dataIndex: 'isEmergencialString',
      sorter: (a, b) => compareText(a.isEmergencialString, b.isEmergencialString),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            style={{ width: 120, marginBottom: 8, display: 'block' }}
            placeholder="Filtrar"
            value={selectedKeys[0]}
            onChange={value => {
              setSelectedKeys(value ? [value] : []);
              confirm();
            }}
            allowClear>
            <Select.Option value="Sim">Sim</Select.Option>
            <Select.Option value="Não">Não</Select.Option>
            <Select.Option value="A Definir">A Definir</Select.Option>
          </Select>
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>
            Limpar
          </Button>
        </div>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });

    columns.push({
      title: 'DATA DA OC',
      dataIndex: 'dataOc',
      sorter: (a, b) => compareDates(a.dataOc, b.dataOc),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker
            placeholder="Selecionar data"
            style={{ width: '100%', marginBottom: 8 }}
            format="DD/MM/YYYY"
            value={selectedKeys[0] ? moment(selectedKeys[0], 'YYYY-MM-DD') : null}
            onChange={date => {
              if (date) {
                setSelectedKeys([date.format('YYYY-MM-DD')]);
                confirm();
              } else {
                setSelectedKeys([]);
                confirm();
              }
            }}
            allowClear
          />
          <Button onClick={() => clearFilters && clearFilters()} size="small" style={{ width: '100%' }}>
            Limpar
          </Button>
        </div>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
    });      

    if (resultaTabelaStatusConcluido) {
      columns.push({
        title: 'Data Conclusão',
        dataIndex: 'updatedAt',
        render: (text, record) => moment(record.updatedAt).format(dateTimeFormat),
        sorter: (a, b) => compareDates(moment(a.updatedAt).format(dateTimeFormat), moment(b.updatedAt).format(dateTimeFormat)),
        defaultSortOrder: 'descend'
      });
    }

    columns.push({
      title: 'Ações',
      fixed: 'right',
      render: (text, record, index) => {
        return getAllButtons(text, record, index);
      },
    });

    return columns;
  }, [optionPropsCompradores, statusOptions]);

  const columnsFornecedor = useMemo(() => {
    const columns: ColumnProps<OrdemCompraResult>[] = ([] = new Array());
    columns.push({
      title: 'OC',
      dataIndex: 'oc',
      width: '4%',
      sorter: (a, b) => compareNumber(a.oc, b.oc),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <Form>
          <Row>
            <FormInput
              placeholder="Pesquisar"
              name="oc"
              style={{ marginTop: '15px' }}
              onChange={e => (setSelectedKeys ? setSelectedKeys(e.target.value ? [e.target.value] : []) : () => {})}
              onPressEnter={() => {
                if (typeof confirm === 'function') confirm();
              }}
            />
          </Row>
          <Row>
            <Col span={6}>
              <Button
                type="primary"
                style={{ marginLeft: '5px' }}
                onClick={() => {
                  if (typeof confirm === 'function') confirm();
                }}
                icon="commons-search"
                size="small">
                Pesquisar
              </Button>
            </Col>
            <Col span={6}>
              <FormResetButton
                size="small"
                style={{ float: 'right', marginRight: '5px', marginBottom: '15px' }}
                beforeReset={() => {
                  if (typeof clearFilters === 'function') clearFilters(selectedKeys || []);
                }}>
                Limpar
              </FormResetButton>
            </Col>
          </Row>
        </Form>
      ),
      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return '';
      },
    });

   columns.push({
      title: 'COMPRADOR',
      dataIndex: 'compradorId',
      sorter: (a, b) => compareText(a.compradorId, b.compradorId),

      filters: optionPropsCompradores || [],

      filterMultiple: true,

      filterIcon: filtered => {
        const classes = filtered ? 'anticon-filter ant-table-filter-selected' : '';
        return <Icon type="search" className={classes} style={{ minWidth: 'auto', border: 'none' }} />;
      },
      onFilter: (value, record) => value != "" && value == record.compradorId,

      render: (text, record, index) => {
        return (
          <Select
            defaultValue={record.compradorId}
            style={{ width: '100%' }}
            onChange={e => {
              record.compradorId = e;
              updateUsuarioCompradorOrdemCompra(record.id, record.compradorId);
            }}
            disabled={!hasPermission(['SAS_ALTERAR_COMPRADOR']) || resultaTabelaStatusConcluido}>
            {optionPropsCompradores.map(option => {
              return <Option value={option.value}>{option.text}</Option>;
            })}
          </Select>
        );
      },
    });

    columns.push({
      title: 'Escopo Resumido',
      dataIndex: 'escopo',
      render: (index, record) => {
        const text = record.escopo || '';
        const truncated = text.length > 6 ? text.slice(0, 6) + '...' : text;
        return <Tooltip title={text}>{truncated}</Tooltip>;
      },
    });

    columns.push({
      title: 'Ações',
      width: '8%',
      render: (text, record, index) => {
        return getAllButtons(text, record, index);
      },
    });

    return columns;
  }, [optionPropsCompradores]);

  const getAllButtons = (text: string, record: OrdemCompraResult, index: number) => {
    const itens = [];

    itens.push(
      <Tooltip title="Ir para Ordem de Compra">
        <Button
          key="editItem"
          icon="arrows-circled_right_2"
          onClick={() => {
            verificaUsuarioPodeAcessarOrdemCompra(record.id).then(response => {
              if (response && response.valid) {
                // window.location.href = `../ordemCompra/${record.id}`;
                const urlBase = window.location.origin;
                window.open(urlBase + `/ordemCompra/${record.id}`);
              }
            });
          }}
        />
      </Tooltip>,
    );

    if (hasPermission(["SAS_ALTERAR_STATUS_ORDEM_COMPRA"])) {
      itens.push(
        <Tooltip title="Alterar Status da Ordem de Compra">
          <Button
            key="editItem"
            icon="editing-pencil"
            onClick={() => {
              setModalAtualizarOrdemCompra(true);
              setOrdemCompraId(record.id);
            }}
          />
        </Tooltip>,
      );
    }

    return itens;
  };

  const verificaGestor = (): boolean => {
    if (
      hasPermissionPerfil([EGuidPerfilAcesso.GestorTecnico]) &&
      !hasPermissionPerfil([EGuidPerfilAcesso.GerenteDeCompras])
    ) {
      return true;
    }
    return false;
  };

const filteredColumns = useMemo(() => {
  return columns.filter(col => {
      const dataIndex = Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex;
    if (!dataIndex) {
      return true;
    }

    return visibleColumns.includes(dataIndex);
  });
}, [columns, visibleColumns]);


  const filteredColumnsFornecedor = useMemo(() => {
    return columnsFornecedor.filter(col => {
      const dataIndex = Array.isArray(col.dataIndex) ? col.dataIndex.join('.') : col.dataIndex;
      if (!dataIndex) {
        return true;
      }
      return visibleColumns.includes(dataIndex);
    });
  }, [columns , visibleColumns]);

  return (
    <>
      <ModalAtualizarStatusOrdemCompra
        ordemCompraId={ordemCompraId}
        exibirModal={modalAtualizarOrdemCompra}
        setExibirModal={setModalAtualizarOrdemCompra}
      />
      <ModalCadastrarConsultiva
        exibirModal={modalCadastrarConsultiva}
        setExibirModal={setModalCadastrarConsultiva}
      />
      <Visibility visible={resultaTabelaStatusConcluido}>
        <RelatorioOrdemCompraFiltros
          onUpdate={onUpdate}
          periodoInicio={periodoInicio}
          periodoFim={periodoFim}
          setPeriodoInicio={setPeriodoInicio}
          setPeriodoFim={setPeriodoFim}
        />
      </Visibility>

      <Widget
        title={widgetTitle}
        actions={[
          
          <Visibility visible={hasPermission(['SAS_CADASTRAR_CONSULTIVA'])}>
           <Tooltip title="Cadastrar Consultiva">
            <Button
              
              id="mrn-btn-cadastrar consultiva"
              type="link"
              title='Cadastrar Consultiva'
                onClick={() => {
              setModalCadastrarConsultiva(true);
              }}
              //icon="files-xls"
              //onClick={() => handleExportar({ filters: tableFilters, sorter: tableSorter })}
              //style={{ marginRight: '8px' }}
              //loading={exportando}
            />
          </Tooltip>
          </Visibility>,
          <Tooltip title="Exportar Ordem de Compra">
            <Button
              id="mrn-btn-exportar-ordem-compra"
              type="link"
              icon="files-xls"
              onClick={() => handleExportar({ filters: tableFilters, sorter: tableSorter })}
              //style={{ marginRight: '8px' }}
              loading={exportando}
            />
          </Tooltip>
        ]}>
        <Collapse bordered={false}>
          <Collapse.Panel key="filtro" header={`Filtro de Colunas`}>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%', marginBottom: 16 }}
              placeholder="Selecionar Colunas"
              value={visibleColumns}
              onChange={values => setVisibleColumns(values)}>
              {columnOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Collapse.Panel>
        </Collapse>

        <Table
          rowKey="id"
          //columns={hasPermissionPerfil([EGuidPerfilAcesso.FornecedorSAS]) ? columnsFornecedor : columns}
          columns={hasPermissionPerfil([EGuidPerfilAcesso.FornecedorSAS]) ? filteredColumnsFornecedor : filteredColumns}
          dataSource={dataSource}
          pagination={{ pageSize: pageSize, total: totalItems }}
          onChange={(pagination: any, filters: any, sorter: any) => {
            setTableFilters(filters);
            setTableSorter(sorter);
            fetchData(pagination, filters, sorter);
          }}
        />
      </Widget>
    </>
  );
};

export default TabelaOrdemCompra;
